import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const METRIC_STYLES = {
    success: {
        iconWrap: 'bg-emerald-500/15 border border-emerald-400/40',
        icon: 'text-emerald-300',
        value: 'text-emerald-300'
    },
    warning: {
        iconWrap: 'bg-amber-500/15 border border-amber-400/40',
        icon: 'text-amber-300',
        value: 'text-amber-300'
    },
    primary: {
        iconWrap: 'bg-cyan-500/15 border border-cyan-400/40',
        icon: 'text-cyan-300',
        value: 'text-cyan-300'
    }
};

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const safeSum = (obj) => {
    if (!obj || typeof obj !== 'object') return 0;
    return Object.values(obj).reduce((a, b) => a + (Number(b) || 0), 0);
};

const prettyKey = (key) =>
    String(key)
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .trim()
        .toLowerCase();

const EmptyState = ({ title = 'Sin datos', desc = 'Ajusta el rango o carga información.' }) => (
    <div className={CARD + ' p-6 flex items-center justify-center'}>
        <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
                <Icon name="Database" size={22} className="text-cyan-300" />
            </div>
            <p className="text-white font-semibold">{title}</p>
            <p className="text-sm text-gray-400 mt-1">{desc}</p>
        </div>
    </div>
);

const Line = ({ formatCurrency, label, value, negative = false }) => (
    <div className="flex items-center justify-between gap-3">
        <span className="text-xs sm:text-sm text-gray-400 capitalize truncate">{label}</span>
        <span
            className={[
                'text-xs sm:text-sm font-medium font-mono',
                negative ? 'text-red-300' : 'text-gray-200'
            ].join(' ')}
        >
            {formatCurrency(value)}
        </span>
    </div>
);

const TotalLine = ({ formatCurrency, label, value, tone = 'primary' }) => {
    const st = METRIC_STYLES[tone] ?? METRIC_STYLES.primary;
    return (
        <div className="flex items-center justify-between pt-3 border-t border-gray-800/80">
            <span className="text-sm font-semibold text-gray-100">{label}</span>
            <span className={`text-sm font-bold font-mono ${st.value}`}>
                {formatCurrency(value)}
            </span>
        </div>
    );
};

/**
 * balanceData esperado:
 * {
 *  assets: { current: {...}, fixed: {...} },
 *  liabilities: { current: {...}, longTerm: {...} },
 *  equity: {...}
 * }
 */
const BalanceSheet = ({
    dateRange,
    onDateRangeChange, // (opcional) por si tienes date picker fuera
    onExport,
    balanceData,       // ✅ YA NO HAY DUMMY: esto viene de tu backend/store
    loading = false,
    error = null,

    // Opcional para formateo:
    locale = 'es-MX',
    defaultCurrency = 'MXN'
}) => {
    const [currency, setCurrency] = useState(defaultCurrency);

    const formatCurrency = (value) =>
        new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0
        }).format(Number(value) || 0);

    const hasData = !!balanceData?.assets || !!balanceData?.liabilities || !!balanceData?.equity;

    const sums = useMemo(() => {
        const currentAssets = safeSum(balanceData?.assets?.current);
        const fixedAssets = safeSum(balanceData?.assets?.fixed);
        const currentLiabilities = safeSum(balanceData?.liabilities?.current);
        const longTermLiabilities = safeSum(balanceData?.liabilities?.longTerm);
        const totalEquity = safeSum(balanceData?.equity);

        const totalAssets = currentAssets + fixedAssets;
        const totalLiabilities = currentLiabilities + longTermLiabilities;

        return {
            currentAssets,
            fixedAssets,
            totalAssets,
            currentLiabilities,
            longTermLiabilities,
            totalLiabilities,
            totalEquity
        };
    }, [balanceData]);

    const metrics = [
        { label: 'Activos Totales', value: sums.totalAssets, icon: 'TrendingUp', tone: 'success' },
        { label: 'Pasivos Totales', value: sums.totalLiabilities, icon: 'TrendingDown', tone: 'warning' },
        { label: 'Patrimonio Neto', value: sums.totalEquity, icon: 'Scale', tone: 'primary' }
    ];

    if (loading) {
        return (
            <div className="space-y-4">
                <div className={PANEL + ' p-5'}>
                    <div className="h-5 w-48 bg-gray-800/70 rounded-lg" />
                    <div className="h-4 w-64 bg-gray-900/70 rounded-lg mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className={CARD + ' p-5'}>
                            <div className="h-11 w-11 bg-gray-800/60 rounded-xl mb-3" />
                            <div className="h-7 w-40 bg-gray-800/60 rounded-lg" />
                            <div className="h-4 w-32 bg-gray-900/60 rounded-lg mt-2" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return <EmptyState title="Error al cargar" desc={String(error)} />;
    }

    if (!hasData) {
        return <EmptyState title="Balance sin datos" desc="No hay información para este rango." />;
    }

    const assetsCurrentEntries = Object.entries(balanceData?.assets?.current ?? {});
    const assetsFixedEntries = Object.entries(balanceData?.assets?.fixed ?? {});
    const liabilitiesCurrentEntries = Object.entries(balanceData?.liabilities?.current ?? {});
    const liabilitiesLongEntries = Object.entries(balanceData?.liabilities?.longTerm ?? {});
    const equityEntries = Object.entries(balanceData?.equity ?? {});

    const currentRatio =
        sums.currentLiabilities > 0 ? (sums.currentAssets / sums.currentLiabilities) : null;

    const debtRatio =
        sums.totalAssets > 0 ? (sums.totalLiabilities / sums.totalAssets) : null;

    const equityAssetsRatio =
        sums.totalAssets > 0 ? (sums.totalEquity / sums.totalAssets) : null;

    const debtToEquity =
        sums.totalEquity > 0 ? (sums.totalLiabilities / sums.totalEquity) : null;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Balance General</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                Al {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Activos • Pasivos • Patrimonio
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        >
                            <option value="MXN">MXN $</option>
                            <option value="USD">USD $</option>
                            <option value="ARS">ARS $</option>
                        </select>

                        <Button
                            variant="outline"
                            onClick={() => onExport?.('balance-sheet')}
                            className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                        >
                            <Icon name="Download" className="w-4 h-4 mr-2" />
                            Exportar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Métricas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metrics.map((m, i) => {
                    const st = METRIC_STYLES[m.tone] ?? METRIC_STYLES.primary;
                    return (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className={CARD + ' p-4 sm:p-5'}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${st.iconWrap}`}>
                                    <Icon name={m.icon} size={20} className={st.icon} />
                                </div>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-white mb-1 font-mono">
                                {formatCurrency(m.value)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-400">{m.label}</p>
                        </motion.div>
                    );
                })}
            </div>

            {/* Balance detallado */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activos */}
                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/15 border border-emerald-400/40">
                            <Icon name="TrendingUp" size={18} className="text-emerald-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Activos</h4>
                            <p className="text-[11px] text-gray-400">Corrientes y fijos</p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div className="bg-gray-950/60 border border-gray-800 rounded-2xl p-3 sm:p-4">
                            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-3">
                                Activos corrientes
                            </p>
                            <div className="space-y-2">
                                {assetsCurrentEntries.length === 0 ? (
                                    <p className="text-xs text-gray-500">Sin rubros</p>
                                ) : (
                                    assetsCurrentEntries.map(([k, v]) => (
                                        <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                                    ))
                                )}
                                <TotalLine
                                    formatCurrency={formatCurrency}
                                    label="Total Activos Corrientes"
                                    value={sums.currentAssets}
                                    tone="primary"
                                />
                            </div>
                        </div>

                        <div className="bg-gray-950/60 border border-gray-800 rounded-2xl p-3 sm:p-4">
                            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-3">
                                Activos fijos
                            </p>
                            <div className="space-y-2">
                                {assetsFixedEntries.length === 0 ? (
                                    <p className="text-xs text-gray-500">Sin rubros</p>
                                ) : (
                                    assetsFixedEntries.map(([k, v]) => (
                                        <Line
                                            key={k}
                                            formatCurrency={formatCurrency}
                                            label={prettyKey(k)}
                                            value={v}
                                            negative={Number(v) < 0}
                                        />
                                    ))
                                )}
                                <TotalLine
                                    formatCurrency={formatCurrency}
                                    label="Total Activos Fijos"
                                    value={sums.fixedAssets}
                                    tone="primary"
                                />
                            </div>
                        </div>

                        <TotalLine
                            formatCurrency={formatCurrency}
                            label="Total Activos"
                            value={sums.totalAssets}
                            tone="success"
                        />
                    </div>
                </div>

                {/* Pasivos y Patrimonio */}
                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/15 border border-amber-400/40">
                            <Icon name="TrendingDown" size={18} className="text-amber-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Pasivos y Patrimonio</h4>
                            <p className="text-[11px] text-gray-400">Corrientes, largo plazo y patrimonio</p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <div className="bg-gray-950/60 border border-gray-800 rounded-2xl p-3 sm:p-4">
                            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-3">
                                Pasivos corrientes
                            </p>
                            <div className="space-y-2">
                                {liabilitiesCurrentEntries.length === 0 ? (
                                    <p className="text-xs text-gray-500">Sin rubros</p>
                                ) : (
                                    liabilitiesCurrentEntries.map(([k, v]) => (
                                        <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                                    ))
                                )}
                                <TotalLine
                                    formatCurrency={formatCurrency}
                                    label="Total Pasivos Corrientes"
                                    value={sums.currentLiabilities}
                                    tone="primary"
                                />
                            </div>
                        </div>

                        <div className="bg-gray-950/60 border border-gray-800 rounded-2xl p-3 sm:p-4">
                            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-3">
                                Pasivos a largo plazo
                            </p>
                            <div className="space-y-2">
                                {liabilitiesLongEntries.length === 0 ? (
                                    <p className="text-xs text-gray-500">Sin rubros</p>
                                ) : (
                                    liabilitiesLongEntries.map(([k, v]) => (
                                        <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                                    ))
                                )}
                                <TotalLine
                                    formatCurrency={formatCurrency}
                                    label="Total Pasivos L/P"
                                    value={sums.longTermLiabilities}
                                    tone="primary"
                                />
                            </div>
                        </div>

                        <div className="bg-gray-950/60 border border-gray-800 rounded-2xl p-3 sm:p-4">
                            <p className="text-xs font-semibold text-white uppercase tracking-wide mb-3">
                                Patrimonio
                            </p>
                            <div className="space-y-2">
                                {equityEntries.length === 0 ? (
                                    <p className="text-xs text-gray-500">Sin rubros</p>
                                ) : (
                                    equityEntries.map(([k, v]) => (
                                        <Line key={k} formatCurrency={formatCurrency} label={prettyKey(k)} value={v} />
                                    ))
                                )}
                                <TotalLine
                                    formatCurrency={formatCurrency}
                                    label="Total Patrimonio"
                                    value={sums.totalEquity}
                                    tone="primary"
                                />
                            </div>
                        </div>

                        <TotalLine
                            formatCurrency={formatCurrency}
                            label="Total Pasivos y Patrimonio"
                            value={sums.totalLiabilities + sums.totalEquity}
                            tone="success"
                        />
                    </div>
                </div>
            </div>

            {/* Indicadores */}
            <div className={CARD + ' p-4 sm:p-6'}>
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-cyan-500/15 border border-cyan-400/40">
                        <Icon name="Activity" size={18} className="text-cyan-300" />
                    </div>
                    <div>
                        <h4 className="text-sm sm:text-base font-semibold text-white">Indicadores de Solvencia</h4>
                        <p className="text-[11px] text-gray-400">Ratios clave</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {[
                        {
                            label: 'Liquidez Corriente',
                            value: currentRatio == null ? '—' : currentRatio.toFixed(2),
                            description: 'Activo Corriente / Pasivo Corriente'
                        },
                        {
                            label: 'Endeudamiento',
                            value: debtRatio == null ? '—' : (debtRatio * 100).toFixed(1) + '%',
                            description: 'Pasivos / Activos'
                        },
                        {
                            label: 'Patrimonio / Activos',
                            value: equityAssetsRatio == null ? '—' : (equityAssetsRatio * 100).toFixed(1) + '%',
                            description: 'Solvencia'
                        },
                        {
                            label: 'Deuda a Patrimonio',
                            value: debtToEquity == null ? '—' : debtToEquity.toFixed(2),
                            description: 'Pasivos / Patrimonio'
                        }
                    ].map((m, idx) => (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.04 }}
                            className="text-center p-3 sm:p-4 bg-gray-950/60 border border-gray-800 rounded-2xl"
                        >
                            <p className="text-lg sm:text-2xl font-bold text-white mb-1 font-mono">{m.value}</p>
                            <p className="text-xs sm:text-sm font-medium text-gray-100">{m.label}</p>
                            <p className="text-[11px] text-gray-400">{m.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BalanceSheet;
